import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Room, Track } from "livekit-client";
import {
  Camera,
  LogOut,
  MessageSquare,
  Mic,
  MicOff,
  Settings,
  Video,
  VideoOff,
  Volume2,
  X,
} from "lucide-react";
import {
  getPreferredDeviceId,
  saveDevicePreference,
} from "@jourfx/ui";

interface DeviceInfo {
  deviceId: string;
  label: string;
}

interface UseDeviceSettingsResult {
  cameras: DeviceInfo[];
  microphones: DeviceInfo[];
  speakers: DeviceInfo[];
  selectedCamera: string;
  selectedMic: string;
  selectedSpeaker: string;
  switchCamera: (deviceId: string) => Promise<void>;
  switchMic: (deviceId: string) => Promise<void>;
  switchSpeaker: (deviceId: string) => Promise<void>;
}

interface CallControlsProps {
  isMicEnabled: boolean;
  isCameraEnabled: boolean;
  onToggleMic: () => void | Promise<void>;
  onToggleCamera: () => void | Promise<void>;
  onOpenSettings: () => void;
  onLeave: () => void;
  children?: ReactNode;
}

interface DeviceSettingsDialogProps {
  open: boolean;
  deviceSettings: UseDeviceSettingsResult;
  onClose: () => void;
}

interface LeaveConfirmDialogProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function useDeviceSettings(room: Room | null): UseDeviceSettingsResult {
  const { t } = useTranslation("video");
  const [cameras, setCameras] = useState<DeviceInfo[]>([]);
  const [microphones, setMicrophones] = useState<DeviceInfo[]>([]);
  const [speakers, setSpeakers] = useState<DeviceInfo[]>([]);
  const [selectedCamera, setSelectedCamera] = useState("");
  const [selectedMic, setSelectedMic] = useState("");
  const [selectedSpeaker, setSelectedSpeaker] = useState("");

  useEffect(() => {
    async function enumerate() {
      if (!navigator.mediaDevices?.enumerateDevices) {
        return;
      }

      try {
        const all = await navigator.mediaDevices.enumerateDevices();
        const clean = (label: string) =>
          label.replace(/\s*\([0-9a-fx:]+\)\s*$/i, "").trim();

        const cams = all
          .filter((d) => d.kind === "videoinput")
          .map((d, i) => ({
            deviceId: d.deviceId,
            label: clean(d.label) || `${t("settings.camera")} ${i + 1}`,
          }));
        const mics = all
          .filter((d) => d.kind === "audioinput")
          .map((d, i) => ({
            deviceId: d.deviceId,
            label: clean(d.label) || `${t("settings.microphone")} ${i + 1}`,
          }));
        const spks = all
          .filter((d) => d.kind === "audiooutput")
          .map((d, i) => ({
            deviceId: d.deviceId,
            label: clean(d.label) || `${t("settings.speaker")} ${i + 1}`,
          }));

        setCameras(cams);
        setMicrophones(mics);
        setSpeakers(spks);

        const activeCamId = room?.localParticipant
          ?.getTrackPublication(Track.Source.Camera)
          ?.track?.mediaStreamTrack?.getSettings()?.deviceId;
        const activeMicId = room?.localParticipant
          ?.getTrackPublication(Track.Source.Microphone)
          ?.track?.mediaStreamTrack?.getSettings()?.deviceId;

        if (!selectedCamera) {
          const preferred = getPreferredDeviceId("camera", cams.map((d) => d.deviceId), activeCamId);
          setSelectedCamera(preferred);
          if (preferred && preferred !== activeCamId && room) {
            room.switchActiveDevice("videoinput", preferred).catch(console.error);
          }
        }
        if (!selectedMic) {
          const preferred = getPreferredDeviceId("microphone", mics.map((d) => d.deviceId), activeMicId);
          setSelectedMic(preferred);
          if (preferred && preferred !== activeMicId && room) {
            room.switchActiveDevice("audioinput", preferred).catch(console.error);
          }
        }
        if (!selectedSpeaker) {
          const preferred = getPreferredDeviceId("speaker", spks.map((d) => d.deviceId));
          setSelectedSpeaker(preferred);
          if (preferred && room) {
            room.switchActiveDevice("audiooutput", preferred).catch(console.error);
          }
        }
      } catch (err) {
        console.error("Failed to enumerate devices:", err);
      }
    }

    void enumerate();
  }, [room, selectedCamera, selectedMic, selectedSpeaker, t]);

  const switchCamera = useCallback(
    async (deviceId: string) => {
      if (!room) return;
      setSelectedCamera(deviceId);
      saveDevicePreference("camera", deviceId);
      try {
        await room.switchActiveDevice("videoinput", deviceId);
      } catch (err) {
        console.error("Failed to switch camera:", err);
      }
    },
    [room]
  );

  const switchMic = useCallback(
    async (deviceId: string) => {
      if (!room) return;
      setSelectedMic(deviceId);
      saveDevicePreference("microphone", deviceId);
      try {
        await room.switchActiveDevice("audioinput", deviceId);
      } catch (err) {
        console.error("Failed to switch microphone:", err);
      }
    },
    [room]
  );

  const switchSpeaker = useCallback(
    async (deviceId: string) => {
      if (!room) return;
      setSelectedSpeaker(deviceId);
      saveDevicePreference("speaker", deviceId);
      try {
        await room.switchActiveDevice("audiooutput", deviceId);
      } catch (err) {
        console.error("Failed to switch speaker:", err);
      }
    },
    [room]
  );

  return {
    cameras,
    microphones,
    speakers,
    selectedCamera,
    selectedMic,
    selectedSpeaker,
    switchCamera,
    switchMic,
    switchSpeaker,
  };
}

export function CallControls({
  isMicEnabled,
  isCameraEnabled,
  onToggleMic,
  onToggleCamera,
  onOpenSettings,
  onLeave,
  children,
}: CallControlsProps) {
  const { t } = useTranslation("video");

  return (
    <div className="relative z-10 flex items-center justify-center gap-2 sm:gap-4 px-3 sm:px-4 py-3 sm:py-4 bg-gradient-to-t from-black/60 to-transparent">
      <button
        onClick={onToggleMic}
        className={`h-10 w-10 sm:h-11 sm:w-11 rounded-full flex items-center justify-center transition-colors shadow-lg ${
          isMicEnabled
            ? "bg-white text-[var(--stone-700)] hover:bg-[var(--stone-100)]"
            : "bg-red-500 text-white hover:bg-red-600"
        }`}
        aria-label={isMicEnabled ? t("controls.mute") : t("controls.unmute")}
      >
        {isMicEnabled ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
      </button>

      <button
        onClick={onToggleCamera}
        className={`h-10 w-10 sm:h-11 sm:w-11 rounded-full flex items-center justify-center transition-colors shadow-lg ${
          isCameraEnabled
            ? "bg-white text-[var(--stone-700)] hover:bg-[var(--stone-100)]"
            : "bg-red-500 text-white hover:bg-red-600"
        }`}
        aria-label={isCameraEnabled ? t("controls.videoOff") : t("controls.videoOn")}
      >
        {isCameraEnabled ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
      </button>

      {children}

      <button
        onClick={onOpenSettings}
        className="h-10 w-10 sm:h-11 sm:w-11 rounded-full flex items-center justify-center transition-colors shadow-lg bg-white/20 text-white hover:bg-white/30"
        aria-label={t("controls.settings")}
      >
        <Settings className="h-5 w-5" />
      </button>

      <button
        onClick={onLeave}
        className="h-10 w-10 sm:h-11 sm:w-auto sm:px-5 rounded-full flex items-center justify-center gap-2 bg-red-600 text-white hover:bg-red-700 transition-colors shadow-lg font-medium text-sm"
        aria-label={t("controls.leave")}
      >
        <LogOut className="h-4 w-4" />
        <span className="hidden sm:inline">{t("controls.leave")}</span>
      </button>
    </div>
  );
}

export function ChatControlButton({
  active,
  unreadCount,
  onClick,
}: {
  active: boolean;
  unreadCount: number;
  onClick: () => void;
}) {
  const { t } = useTranslation("video");

  return (
    <button
      onClick={onClick}
      className={`h-10 w-10 sm:h-11 sm:w-11 rounded-full flex items-center justify-center transition-colors shadow-lg relative ${
        active
          ? "bg-white text-[var(--stone-700)]"
          : "bg-white/20 text-white hover:bg-white/30"
      }`}
      aria-label={t("controls.chat")}
    >
      <MessageSquare className="h-5 w-5" />
      {unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1">
          {unreadCount}
        </span>
      )}
    </button>
  );
}

export function DeviceSettingsDialog({
  open,
  deviceSettings,
  onClose,
}: DeviceSettingsDialogProps) {
  const { t } = useTranslation("video");

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <button
        type="button"
        aria-label={t("settings.close")}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-stone-800 flex items-center gap-2">
            <Settings className="w-5 h-5 text-stone-500" />
            {t("settings.title")}
          </h2>
          <button
            onClick={onClose}
            className="text-stone-400 hover:text-stone-600 transition-colors"
            aria-label={t("settings.close")}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-sm font-medium text-stone-700">
              <Camera className="w-4 h-4 text-stone-400" />
              {t("settings.camera")}
            </label>
            <select
              className="w-full p-2.5 rounded-xl border border-stone-200 bg-stone-50 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-stone-300 focus:border-stone-300 transition-colors"
              value={deviceSettings.selectedCamera}
              onChange={(e) => void deviceSettings.switchCamera(e.target.value)}
            >
              {deviceSettings.cameras.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-sm font-medium text-stone-700">
              <Mic className="w-4 h-4 text-stone-400" />
              {t("settings.microphone")}
            </label>
            <select
              className="w-full p-2.5 rounded-xl border border-stone-200 bg-stone-50 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-stone-300 focus:border-stone-300 transition-colors"
              value={deviceSettings.selectedMic}
              onChange={(e) => void deviceSettings.switchMic(e.target.value)}
            >
              {deviceSettings.microphones.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label}
                </option>
              ))}
            </select>
          </div>

          {deviceSettings.speakers.length > 0 && (
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 text-sm font-medium text-stone-700">
                <Volume2 className="w-4 h-4 text-stone-400" />
                {t("settings.speaker")}
              </label>
              <select
                className="w-full p-2.5 rounded-xl border border-stone-200 bg-stone-50 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-stone-300 focus:border-stone-300 transition-colors"
                value={deviceSettings.selectedSpeaker}
                onChange={(e) => void deviceSettings.switchSpeaker(e.target.value)}
              >
                {deviceSettings.speakers.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <button
          onClick={onClose}
          className="mt-6 w-full px-6 py-3 rounded-xl font-semibold text-stone-700 bg-stone-100 hover:bg-stone-200 transition-colors"
        >
          {t("settings.close")}
        </button>
      </div>
    </div>
  );
}

export function LeaveConfirmDialog({
  open,
  onCancel,
  onConfirm,
}: LeaveConfirmDialogProps) {
  const { t } = useTranslation("video");

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <button
        type="button"
        aria-label={t("controls.leaveConfirm.cancel")}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onCancel}
      />

      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 animate-in fade-in zoom-in-95 duration-200">
        <div className="flex justify-center mb-4">
          <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center">
            <LogOut className="w-7 h-7 text-red-600" />
          </div>
        </div>

        <h2 className="text-xl font-bold text-[var(--stone-800)] text-center mb-2">
          {t("controls.leaveConfirm.title")}
        </h2>

        <p className="text-[var(--stone-600)] text-center mb-6">
          {t("controls.leaveConfirm.message")}
        </p>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-6 py-3 rounded-xl font-semibold text-[var(--stone-700)] bg-[var(--stone-100)] hover:bg-[var(--stone-200)] transition-colors"
          >
            {t("controls.leaveConfirm.cancel")}
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-6 py-3 rounded-xl font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors"
          >
            {t("controls.leaveConfirm.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}
